import { Play as PlayIcon } from "@hl/base-components/lib/assets/icons.generated";
import { OUTLINE_COLOR } from "@hl/base-components/lib/theme/button";
import { buildOpenSeaLinkUrl2 } from "@hl/shared-features/lib/utils/opensea";
import { isAudioUrl } from "@hl/shared-features/lib/utils/urls";
import {
  Box,
  Button,
  Card,
  createStyles,
  Group,
  Text,
  Space,
  UnstyledButton,
} from "@mantine/core";
import { useHover } from "@mantine/hooks";
import { Link } from "react-router-dom";

import { PortfolioItem } from "../../apollo/graphql.generated";
import { ReactComponent as ExpandIcon } from "../../assets/icons/expand-icon.svg";
import { MINT_BASE_URL } from "../../config";
import { normalizeNftUrl } from "../../utils/image-utils";

const useStyle = createStyles((theme) => ({
  card: {
    width: "auto",

    [`@media (min-width: ${theme.breakpoints.sm}px)`]: {
      maxWidth: 347,
    },
  },
  imageBox: {
    position: "relative",
    display: "block",
  },
  image: {
    width: "100%",
    objectFit: "scale-down",
    cursor: "pointer",
    [`@media (min-width: ${theme.breakpoints.sm}px)`]: {
      height: 347,
    },
  },
  detailsBtn: {
    position: "absolute",
    bottom: 10,
    right: 10,
    opacity: 1,
    border: "none",
    boxShadow: theme.shadows.xs,
  },
  audioButton: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 40,
    height: 40,
    backgroundColor: theme.colors.white[6],
    borderRadius: "100%",

    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
}));

type Props = {
  item: PortfolioItem;
  href: string;
};

export const PortfolioTile = ({ item, href }: Props) => {
  const { classes } = useStyle();
  const { hovered, ref } = useHover();

  const name = item.name ?? item.collectionName ?? "";

  const col = item.collectionInfo;
  const isAudio = isAudioUrl(item.videoUrl);
  return (
    <Box p={8} className={classes.card}>
      <Card.Section ref={ref}>
        <UnstyledButton component={Link} to={href} className={classes.imageBox}>
          {item.videoUrl && !isAudio ? (
            <video
              className={classes.image}
              controls={false}
              autoPlay
              loop
              muted
              playsInline
            >
              <source src={item.videoUrl} />
            </video>
          ) : (
            <>
              <img
                className={classes.image}
                src={normalizeNftUrl(item.imageUrl)}
                alt={name}
              />
              {isAudio && (
                <Box className={classes.audioButton} pl={3}>
                  <PlayIcon width={20} />
                </Box>
              )}
            </>
          )}
          {!isAudio && (
            <Button
              leftIcon={<ExpandIcon />}
              style={{ visibility: hovered ? "visible" : "hidden" }}
              className={classes.detailsBtn}
              color={OUTLINE_COLOR}
            >
              Details
            </Button>
          )}
        </UnstyledButton>
      </Card.Section>
      <Space h="md" />
      <Text weight={500} size={16}>
        {name}
      </Text>
      {item.collectionName && item.collectionName !== name && (
        <Text weight={400} size={16}>
          {item.collectionName}
        </Text>
      )}
      <Group pt={20}>
        {item.collectionId && (
          <Button
            component="a"
            href={MINT_BASE_URL + `/${item.collectionId}`}
            target="_blank"
            color="gray.0"
          >
            View mint page
          </Button>
        )}
        {item.openSeaLink && (
          <Button
            component="a"
            href={buildOpenSeaLinkUrl2(col.address, col.chainId, col.tokenId)}
            target="_blank"
            color="gray.0"
          >
            View on OpenSea
          </Button>
        )}
        {item.button && (
          <Button
            component="a"
            href={item.button.url}
            target="_blank"
            color="gray.0"
          >
            {item.button.text}
          </Button>
        )}
      </Group>
    </Box>
  );
};
